/**
 * The scrubber (docs/SPEC.md §7.4, §7.5, §8.2, §10): the film's scenes as
 * named segments under the player.
 *
 * - **A tap on a segment** focuses that scene (it loops; the track shows its
 *   moments); a second tap, or a tap on the film's name, is Back to the film.
 * - **Dragging a segment** onto another moves the scene: the page sends the
 *   change "Move scene 3 before scene 1" as words (§7.4: the same as saying
 *   it). Alt with ← or → on a focused segment does the same from the keys.
 * - **The track** under the segments: a click seeks; a drag marks a part of
 *   one scene (§8.2's `span`), shown until cleared.
 * - A locked scene (§10) carries a lock and says so.
 * - **The strip** (§6.1, P28-T09): each segment shows its scene's tile, a
 *   frame the player drew in the browser (`tiles`), grey until the scene is
 *   built; while the film builds (`building`) a scene not built yet cannot
 *   be picked and there is no film to go back to.
 *
 * Controlled: the clock and the part come in, every act goes out.
 */
import { type KeyboardEvent, type PointerEvent, useRef, useState } from "react";
import type { Span } from "../api/changes";
import { SCRUB, fillAll, secondsText } from "../copy/changes";
import { formatTime } from "../film/play";
import { LockIcon, buttonClass } from "../ui";
import {
  type ScrubScene,
  fractionOf,
  lengthOf,
  moveTarget,
  slotAt,
  spanBetween,
  timeAt,
  trackRange,
} from "./scrub";

export interface ScrubberProps {
  /** The film's name: a tap on it is Back to the film. */
  title: string;
  scenes: ScrubScene[];
  /** The focused scene's id. */
  focus: string | null;
  /** Film time of the moment on screen. */
  filmTime: number;
  playing: boolean;
  span: Span | null;
  onToggle(): void;
  onSeek(filmTime: number): void;
  onFocus(sceneId: string | null): void;
  onSpan(span: Span | null): void;
  /** Move `sceneId` before `beforeId` (null: to the end). */
  onMove(sceneId: string, beforeId: string | null): void;
  /** Moves are not offered (a change is under way). */
  moveDisabled?: boolean;
  /** Each scene's tile: a frame's address by scene id. */
  tiles?: Record<string, string>;
  /** The film is being built: only built scenes play, alone. */
  building?: boolean;
}

/** How far a press must travel before it is a drag, CSS pixels. */
const DRAG_PX = 6;

const say = (s: string, p: Record<string, unknown>) => fillAll(s, p) ?? s;

export function Scrubber(props: ScrubberProps) {
  const { scenes, focus, span } = props;
  const range = trackRange(scenes, focus);
  const length = lengthOf(scenes);
  const listRef = useRef<HTMLOListElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const press = useRef<{
    index: number;
    x: number;
    dragging: boolean;
  } | null>(null);
  const [drag, setDrag] = useState<{ from: number; to: number } | null>(null);
  const trackPress = useRef<{ x: number; t: number; moved: boolean } | null>(
    null,
  );
  const [draft, setDraft] = useState<Span | null>(null);

  const focusedIndex = focus ? scenes.findIndex((s) => s.id === focus) : -1;
  const shownTime = focus ? props.filmTime - range[0] : props.filmTime;
  const shownLength = focus ? range[1] - range[0] : length;

  // ---- the segments: tap = focus, drag = move ------------------------------

  const bounds = (): [number, number][] =>
    Array.from(listRef.current?.children ?? []).map((li) => {
      const r = (li as HTMLElement).getBoundingClientRect();
      return [r.left, r.right];
    });

  const segmentDown = (index: number) => (e: PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    press.current = { index, x: e.clientX, dragging: false };
  };
  const segmentMove = (e: PointerEvent<HTMLElement>) => {
    const p = press.current;
    if (!p || props.moveDisabled) return;
    if (!p.dragging && Math.abs(e.clientX - p.x) < DRAG_PX) return;
    if (!p.dragging) {
      p.dragging = true;
      try {
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      } catch {
        // capture is a nicety
      }
    }
    setDrag({ from: p.index, to: slotAt(bounds(), e.clientX) });
  };
  const segmentUp = (index: number) => (e: PointerEvent<HTMLElement>) => {
    const p = press.current;
    press.current = null;
    if (p?.dragging) {
      const to = slotAt(bounds(), e.clientX);
      setDrag(null);
      const target = moveTarget(scenes, p.index, to);
      if (target) props.onMove(scenes[p.index].id, target.before);
      return;
    }
    if (p && p.index === index) tap(index);
  };
  const tap = (index: number) => {
    if (!scenes[index].ready) return;
    if (props.building) props.onFocus(scenes[index].id);
    else props.onFocus(focusedIndex === index ? null : scenes[index].id);
  };
  const segmentKey = (index: number) => (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      tap(index);
      return;
    }
    if (!e.altKey || props.moveDisabled) return;
    const to =
      e.key === "ArrowLeft"
        ? index - 1
        : e.key === "ArrowRight"
          ? index + 1
          : -2;
    if (to === -2) return;
    e.preventDefault();
    const target = moveTarget(scenes, index, to);
    if (target) props.onMove(scenes[index].id, target.before);
  };

  // ---- the track: click = seek, drag = a part --------------------------------

  const timeOfX = (x: number): number => {
    const r = trackRef.current?.getBoundingClientRect();
    const w = r && r.width > 0 ? r.width : 1;
    return timeAt((x - (r?.left ?? 0)) / w, range);
  };
  const trackDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    trackPress.current = { x: e.clientX, t: timeOfX(e.clientX), moved: false };
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      // capture is a nicety
    }
  };
  const trackMove = (e: PointerEvent<HTMLDivElement>) => {
    const p = trackPress.current;
    if (!p) return;
    if (!p.moved && Math.abs(e.clientX - p.x) < DRAG_PX) return;
    p.moved = true;
    setDraft(spanBetween(scenes, focus, p.t, timeOfX(e.clientX)));
  };
  const trackUp = (e: PointerEvent<HTMLDivElement>) => {
    const p = trackPress.current;
    trackPress.current = null;
    if (!p) return;
    if (p.moved) {
      setDraft(null);
      const marked = spanBetween(scenes, focus, p.t, timeOfX(e.clientX));
      if (marked) props.onSpan(marked);
      return;
    }
    props.onSeek(p.t);
  };
  const trackKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 1 : 0.1;
    if (e.key === "ArrowLeft") props.onSeek(props.filmTime - step);
    else if (e.key === "ArrowRight") props.onSeek(props.filmTime + step);
    else return;
    e.preventDefault();
  };

  const shownSpan = draft ?? span;
  const spanScene = shownSpan
    ? scenes.find((s) => s.id === shownSpan.scene)
    : undefined;
  const spanBox =
    shownSpan && spanScene
      ? {
          left: fractionOf(spanScene.start + shownSpan.t0, range),
          right: fractionOf(spanScene.start + shownSpan.t1, range),
        }
      : null;
  const spanNumber = spanScene ? scenes.indexOf(spanScene) + 1 : 0;

  return (
    <div className="flex w-full flex-col gap-2" data-testid="scrubber">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <button
          type="button"
          className={`${buttonClass({ variant: "secondary", size: "sm" })} min-h-[44px] min-w-[5.5rem] md:min-h-0`}
          onClick={props.onToggle}
        >
          {props.playing ? SCRUB.pause : SCRUB.play}
        </button>
        <span
          className="tabular-nums text-slate-600"
          data-testid="scrub-time"
          aria-live="off"
        >
          {say(SCRUB.time, {
            now: formatTime(shownTime),
            length: formatTime(shownLength),
          })}
        </span>
        <button
          type="button"
          data-testid="film-name"
          className={[
            "ml-auto max-w-full truncate rounded-pill px-3 py-1 text-sm",
            focus && !props.building
              ? "bg-brand-tint/50 font-semibold text-brand-dark underline"
              : "text-slate-500",
          ].join(" ")}
          aria-label={focus && !props.building ? SCRUB.backToFilm : props.title}
          title={focus && !props.building ? SCRUB.backToFilm : undefined}
          disabled={!focus || props.building}
          onClick={() => props.onFocus(null)}
        >
          {focus && !props.building ? `← ${props.title}` : props.title}
        </button>
      </div>

      <ol
        ref={listRef}
        aria-label={SCRUB.scenes}
        className="m-0 flex w-full list-none gap-1 p-0"
        title={props.moveDisabled ? undefined : SCRUB.moveHint}
      >
        {scenes.map((scene, i) => {
          const focused = i === focusedIndex;
          const dragged = drag?.from === i;
          const dropOn = drag && drag.to === i && drag.from !== i;
          const label = !scene.ready
            ? say(SCRUB.notBuilt, { n: i + 1, name: scene.name })
            : focused
              ? say(props.building ? SCRUB.playingAlone : SCRUB.focused, {
                  n: i + 1,
                  name: scene.name,
                })
              : say(SCRUB.focusOn, { n: i + 1, name: scene.name });
          const tile = props.tiles?.[scene.id];
          return (
            <li
              key={scene.id}
              style={{ flexGrow: scene.seconds, flexBasis: 0 }}
              className="min-w-0"
              data-testid={`segment-${scene.id}`}
              data-locked={scene.locked ? "true" : undefined}
              data-ready={scene.ready ? "true" : "false"}
            >
              <button
                type="button"
                aria-pressed={focused}
                aria-disabled={!scene.ready || undefined}
                aria-label={
                  scene.locked
                    ? `${label} ${say(SCRUB.locked, { n: i + 1 })}`
                    : label
                }
                onPointerDown={segmentDown(i)}
                onPointerMove={segmentMove}
                onPointerUp={segmentUp(i)}
                onPointerCancel={() => {
                  press.current = null;
                  setDrag(null);
                }}
                onKeyDown={segmentKey(i)}
                className={[
                  "flex min-h-[44px] w-full touch-none select-none flex-col items-start justify-center gap-0.5 overflow-hidden rounded-md border text-left text-xs transition-colors md:min-h-[40px]",
                  focused
                    ? "border-brand bg-brand text-white"
                    : scene.ready
                      ? "border-slate-200 bg-slate-100 text-slate-700 hover:border-brand-light"
                      : "cursor-default border-slate-200 bg-slate-50 text-slate-400",
                  props.tiles ? "p-1" : "px-2 py-1",
                  dragged ? "opacity-50" : "",
                  dropOn ? "ring-2 ring-brand ring-offset-1" : "",
                ].join(" ")}
              >
                {props.tiles ? (
                  tile && scene.ready ? (
                    <img
                      src={tile}
                      alt=""
                      draggable={false}
                      className="block h-14 w-full rounded object-cover md:h-20"
                      data-testid={`tile-${scene.id}`}
                    />
                  ) : (
                    <span
                      aria-hidden="true"
                      className={`block h-14 w-full rounded md:h-20 ${scene.ready ? "bg-slate-300" : "animate-pulse bg-slate-200"}`}
                      data-testid={`tile-${scene.id}`}
                    />
                  )
                ) : null}
                <span
                  className={`flex w-full min-w-0 items-center gap-1 ${props.tiles ? "px-1" : ""}`}
                >
                  <span className="shrink-0 font-semibold tabular-nums">
                    {i + 1}
                  </span>
                  {scene.locked ? (
                    <span
                      className="inline-flex shrink-0"
                      data-testid={`lock-${scene.id}`}
                      title={say(SCRUB.locked, { n: i + 1 })}
                    >
                      <LockIcon size={12} aria-hidden="true" />
                    </span>
                  ) : null}
                  <span className="truncate">{scene.name}</span>
                </span>
                <span
                  className={`hidden tabular-nums sm:inline ${props.tiles ? "px-1" : ""} ${focused ? "text-white/80" : "text-slate-500"}`}
                >
                  {say(SCRUB.seconds, { s: secondsText(scene.seconds) })}
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      <div
        ref={trackRef}
        role="slider"
        tabIndex={0}
        aria-label={
          focus ? say(SCRUB.sceneTrack, { n: focusedIndex + 1 }) : SCRUB.track
        }
        aria-valuemin={0}
        aria-valuemax={Math.round(shownLength * 10) / 10}
        aria-valuenow={Math.round(shownTime * 10) / 10}
        aria-valuetext={formatTime(shownTime)}
        data-testid="scrub-track"
        title={SCRUB.marking}
        onPointerDown={trackDown}
        onPointerMove={trackMove}
        onPointerUp={trackUp}
        onPointerCancel={() => {
          trackPress.current = null;
          setDraft(null);
        }}
        onKeyDown={trackKey}
        className="relative h-6 w-full cursor-pointer touch-none select-none rounded-md bg-slate-100"
      >
        {!focus
          ? scenes
              .slice(1)
              .map((scene) => (
                <span
                  key={scene.id}
                  aria-hidden="true"
                  className="absolute top-1 h-4 w-px bg-slate-300"
                  style={{ left: `${fractionOf(scene.start, range) * 100}%` }}
                />
              ))
          : null}
        {spanBox ? (
          <span
            aria-hidden="true"
            data-testid="scrub-span"
            className="absolute inset-y-0 rounded-sm bg-brand/25 ring-1 ring-brand"
            style={{
              left: `${spanBox.left * 100}%`,
              width: `${Math.max(0.5, (spanBox.right - spanBox.left) * 100)}%`,
            }}
          />
        ) : null}
        <span
          aria-hidden="true"
          data-testid="scrub-head"
          className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-brand-dark"
          style={{ left: `${fractionOf(props.filmTime, range) * 100}%` }}
        />
      </div>

      {span && spanScene ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
          <span data-testid="span-line">
            {say(SCRUB.part, {
              t0: secondsText(span.t0),
              t1: secondsText(span.t1),
              n: spanNumber,
            })}
          </span>
          <button
            type="button"
            className="underline"
            onClick={() => props.onSpan(null)}
          >
            {SCRUB.clearPart}
          </button>
        </div>
      ) : null}
    </div>
  );
}
